import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  NumberInput,
  NumberInputField,
  Spinner,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';

const API_HEADERS = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const PassageOutboundTab = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [minConfidencePercent, setMinConfidencePercent] = useState(0);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/passage-outbound', { headers: API_HEADERS() });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Não foi possível carregar');
      setCameras(Array.isArray(body.data?.cameras) ? body.data.cameras : []);
      setMinConfidencePercent(Number(body.data?.minConfidencePercent) || 0);
    } catch (error) {
      toast({ title: error.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const allSelected = cameras.length > 0 && cameras.every((camera) => camera.enabled);

  const setAll = (enabled) => {
    setCameras((current) => current.map((camera) => ({ ...camera, enabled })));
  };

  const toggleCamera = (id, enabled) => {
    setCameras((current) => current.map((camera) => (
      camera.id === id ? { ...camera, enabled } : camera
    )));
  };

  const setUniqueCode = (id, uniqueCode) => {
    setCameras((current) => current.map((camera) => (
      camera.id === id ? { ...camera, uniqueCode } : camera
    )));
  };

  const save = async () => {
    try {
      setSaving(true);
      const cameraCodes = {};
      cameras.forEach((camera) => {
        const code = String(camera.uniqueCode || '').trim();
        if (code) cameraCodes[camera.id] = code;
      });
      const response = await fetch('/api/passage-outbound', {
        method: 'PUT',
        headers: API_HEADERS(),
        body: JSON.stringify({
          cameraIds: cameras.filter((camera) => camera.enabled).map((camera) => camera.id),
          minConfidencePercent,
          cameraCodes,
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Não foi possível salvar');
      setCameras(Array.isArray(body.data?.cameras) ? body.data.cameras : []);
      setMinConfidencePercent(Number(body.data?.minConfidencePercent) || 0);
      toast({ title: 'Envio de passagens salvo', status: 'success', duration: 3000 });
    } catch (error) {
      toast({ title: error.message, status: 'error', duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box bg="white" borderRadius="md" boxShadow="sm" p={6} w="100%">
      <Heading size="md" mb={2}>Envio de passagens</Heading>
      <Text color="gray.600" mb={4}>
        Escolha quais câmeras enviam a passagem para a integração configurada, e a partir de qual confiança.
        A lista mostra só câmeras com Leitura de placas/passagens ativa.
      </Text>

      {loading ? (
        <Flex justify="center" py={8}>
          <Spinner />
        </Flex>
      ) : (
        <VStack align="stretch" spacing={4}>
          <FormControl>
            <FormLabel>Confiança mínima</FormLabel>
            <NumberInput
              min={0}
              max={100}
              value={minConfidencePercent}
              onChange={(value) => setMinConfidencePercent(value === '' ? 0 : Number(value))}
              maxW="140px"
            >
              <NumberInputField />
            </NumberInput>
            <FormHelperText>
              Envia passagens com confiança igual ou maior que este percentual. 0 envia todas.
            </FormHelperText>
          </FormControl>

          {cameras.length === 0 ? (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                Nenhuma câmera com Leitura de placas/passagens ativa.
              </AlertDescription>
            </Alert>
          ) : (
            <VStack align="stretch" spacing={3}>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0" fontWeight="semibold">Selecionar todas</FormLabel>
                <Switch
                  colorScheme="teal"
                  isChecked={allSelected}
                  onChange={(event) => setAll(event.target.checked)}
                />
              </FormControl>
              <Flex px={1} py={1} align="center" gap={4} display={{ base: 'none', md: 'flex' }}>
                <Text flex="1" fontSize="sm" fontWeight="semibold" color="gray.600">Câmera</Text>
                <Text w="280px" fontSize="sm" fontWeight="semibold" color="gray.600">Código único</Text>
                <Text w="70px" fontSize="sm" fontWeight="semibold" color="gray.600" textAlign="right">Enviar</Text>
              </Flex>
              <Divider />
              {cameras.map((camera) => (
                <Flex key={camera.id} py={2} align="center" gap={4} wrap="wrap">
                  <Box flex="1" minW="180px">
                    <Text fontWeight="medium">{camera.name}</Text>
                    {(camera.latitude != null || camera.longitude != null) && (
                      <Text fontSize="xs" color="gray.500">
                        {camera.latitude ?? '—'}, {camera.longitude ?? '—'}
                      </Text>
                    )}
                  </Box>
                  <Input
                    w={{ base: '100%', md: '280px' }}
                    size="sm"
                    placeholder="Código único"
                    value={camera.uniqueCode || ''}
                    onChange={(event) => setUniqueCode(camera.id, event.target.value)}
                    aria-label={`Código único de ${camera.name}`}
                  />
                  <Switch
                    colorScheme="teal"
                    isChecked={camera.enabled}
                    aria-label={`Enviar passagens de ${camera.name}`}
                    onChange={(event) => toggleCamera(camera.id, event.target.checked)}
                  />
                </Flex>
              ))}
            </VStack>
          )}

          <Button
            alignSelf="flex-start"
            colorScheme="teal"
            onClick={save}
            isLoading={saving}
          >
            Salvar
          </Button>
        </VStack>
      )}
    </Box>
  );
};

export default PassageOutboundTab;
